import React, { useCallback, useMemo, useState } from 'react';
import { useT } from '../i18n';
import { useSession } from '../context/Session';
import { Empty, Icon, Loading } from '../components/ui';
import { adminTrainers } from '../lib/cloud';

// Admin panel: trainers with at least one active trainee, and those trainees
// (fitviz_cloud admin_trainers.list_trainers).

function planLabel(t, subscription) {
  return String(subscription || 't0').toLowerCase() === 't0' ? t('freePlan') : t('premiumPlan');
}

function matches(u, q) {
  return [u.name, u.email].some((s) => s && s.toLowerCase().includes(q));
}

function Person({ user }) {
  return (
    <>
      <div className="gm-strong">{user.name || '—'}</div>
      <div className="gm-small gm-muted">{user.email || user.uid}</div>
    </>
  );
}

function TrainerRow({ trainer, open, onToggle }) {
  const t = useT();
  return (
    <>
      <tr className="gm-admin-row--click" onClick={onToggle}>
        <td>
          <button
            type="button"
            className="gm-icon-btn gm-icon-btn--sm"
            aria-expanded={open}
            aria-label={trainer.name || trainer.email || trainer.uid}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          >
            <Icon name={open ? 'expand_less' : 'expand_more'} size={20} />
          </button>
        </td>
        <td>
          <Person user={trainer} />
          {String(trainer.role || '').toLowerCase() !== 'trainer' && (
            <div className="gm-small gm-error-text">{t('adminTrNotTrainerRole')}</div>
          )}
        </td>
        <td>{planLabel(t, trainer.subscription)}</td>
        <td className="gm-strong">{trainer.trainees.length}</td>
      </tr>
      {open && (
        <tr className="gm-admin-subrow">
          <td />
          <td colSpan={3}>
            <table className="gm-admin-table">
              <thead>
                <tr><th>{t('trainee')}</th><th>{t('adminPlan')}</th><th>{t('adminLbLevel')}</th></tr>
              </thead>
              <tbody>
                {trainer.trainees.map((u) => (
                  <tr key={u.uid} className={u.linked ? undefined : 'gm-admin-row--dim'}>
                    <td>
                      <Person user={u} />
                      {!u.linked && (
                        <div className="gm-small gm-error-text">
                          {u.trainer_email ? t('adminTrOtherTrainer', u.trainer_email) : t('adminTrNoTrainerEmail')}
                        </div>
                      )}
                    </td>
                    <td>{planLabel(t, u.subscription)}</td>
                    <td>{u.level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminTrainers() {
  const t = useT();
  const { refreshAdmin } = useSession();
  // Loads only on Refresh (also the first time): undefined = not loaded yet, null = loading.
  const [trainers, setTrainers] = useState(undefined);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    setTrainers(null);
    setError('');
    try {
      let res = await adminTrainers();
      if (res.error === 'forbidden') {
        // Claim may have been granted after this session's token was minted.
        if (await refreshAdmin(true)) res = await adminTrainers();
      }
      if (res.trainers) setTrainers(res.trainers);
      else {
        setTrainers([]);
        setError(res.error === 'forbidden' ? t('anErrorOccurred') : String(res.error || ''));
      }
    } catch (e) {
      setTrainers([]);
      setError(t('anErrorOccurred'));
    }
  }, [refreshAdmin, t]);

  const q = query.trim().toLowerCase();
  // While searching, a trainer shows when they or one of their trainees match;
  // a trainee match opens the trainer so the hit is visible.
  const shown = useMemo(() => {
    if (!trainers) return [];
    if (!q) return trainers.map((tr) => ({ trainer: tr, hit: false }));
    return trainers
      .map((tr) => ({ trainer: tr, hit: tr.trainees.some((u) => matches(u, q)) }))
      .filter(({ trainer, hit }) => hit || matches(trainer, q));
  }, [trainers, q]);

  const toggle = (uid) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    return next;
  });

  const traineeCount = trainers ? trainers.reduce((n, tr) => n + tr.trainees.length, 0) : 0;

  return (
    <div className="gm-stack">
      <div className="gm-row" style={{ justifyContent: 'space-between', gap: 8 }}>
        <p className="gm-small gm-muted">{t('adminTrHint')}</p>
        <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={load}>
          <Icon name="refresh" size={18} /> {t('adminRefresh')}
        </button>
      </div>
      <section className="gm-card gm-card--pad-lg gm-stack-sm">
        {trainers === undefined && <Empty icon="refresh">{t('adminPressRefresh')}</Empty>}
        {trainers === null && <Loading />}
        {error && <p className="gm-error-text gm-small">{error}</p>}
        {trainers && trainers.length === 0 && !error && <Empty icon="sports">{t('adminTrEmpty')}</Empty>}
        {trainers && trainers.length > 0 && (
          <>
            <div className="gm-row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <input
                className="gm-input"
                style={{ maxWidth: 320 }}
                type="search"
                value={query}
                placeholder={t('adminTrSearch')}
                aria-label={t('adminTrSearch')}
                onChange={(e) => setQuery(e.target.value)}
              />
              <span className="gm-small gm-muted">{t('adminTrSummary', trainers.length, traineeCount)}</span>
            </div>
            {shown.length === 0 ? (
              <Empty icon="search_off">{t('adminTrNoMatch')}</Empty>
            ) : (
              <div className="gm-admin-table-wrap">
                <table className="gm-admin-table">
                  <thead>
                    <tr>
                      <th aria-hidden="true" />
                      <th>{t('trainer')}</th>
                      <th>{t('adminPlan')}</th>
                      <th>{t('adminTrCount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map(({ trainer, hit }) => (
                      <TrainerRow
                        key={trainer.uid}
                        trainer={trainer}
                        open={hit || expanded.has(trainer.uid)}
                        onToggle={() => toggle(trainer.uid)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
